import React from 'react';
import { cn } from '../lib/utils';
import { AlertTriangle, AlertCircle, Info, ChevronRight } from 'lucide-react';

export type AlertSeverity = 'critical' | 'warning' | 'info';

export interface AlertItem {
  severity: AlertSeverity;
  message: string;
  count: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface AlertPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  alerts: AlertItem[];
  maxVisible?: number;
}

const severityConfig: Record<
  AlertSeverity,
  { bg: string; border: string; text: string; icon: typeof AlertTriangle; dot: string }
> = {
  critical: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
    icon: AlertTriangle,
    dot: 'bg-red-500',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
    icon: AlertCircle,
    dot: 'bg-amber-500',
  },
  info: {
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    text: 'text-indigo-800',
    icon: Info,
    dot: 'bg-indigo-500',
  },
};

function AlertPanel({ alerts, maxVisible = 5, className, ...props }: AlertPanelProps) {
  if (!alerts || alerts.length === 0) return null;

  const visibleAlerts = alerts.slice(0, maxVisible);

  return (
    <div
      className={cn(
        'overflow-hidden rounded-card border border-border bg-surface-sheet',
        className,
      )}
      {...props}
    >
      <div className="divide-y divide-slate-100">
        {visibleAlerts.map((alert, idx) => {
          const config = severityConfig[alert.severity] ?? severityConfig.info;
          const Icon = config.icon;

          return (
            <div
              key={idx}
              className={cn('flex items-center justify-between gap-4 px-4 py-3', config.bg)}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-control',
                    config.bg,
                    config.border,
                    'border',
                  )}
                >
                  <Icon className={cn('h-4 w-4', config.text)} />
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={cn(
                      'inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-bold text-white',
                      config.dot,
                    )}
                  >
                    {alert.count}
                  </span>
                  <span className={cn('text-sm font-medium', config.text, 'truncate')}>
                    {alert.message}
                  </span>
                </div>
              </div>

              {alert.action && (
                <button
                  onClick={alert.action.onClick}
                  className={cn(
                    'inline-flex shrink-0 items-center gap-1 rounded-control px-3 py-1.5 text-xs font-semibold transition-colors',
                    config.text,
                    'hover:bg-white/60',
                  )}
                >
                  {alert.action.label}
                  <ChevronRight className="h-3 w-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {alerts.length > maxVisible && (
        <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-center text-xs text-slate-500">
          +{alerts.length - maxVisible} more alerts
        </div>
      )}
    </div>
  );
}

export { AlertPanel };
